import {
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    forwardRef,
    Injector,
    DoCheck,
    Output,
    EventEmitter,
} from '@angular/core';
import { map, take, tap } from 'rxjs/operators';
import { ChoiceListService } from 'src/app/acl-data/choice-list/choice-list.service';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    NgControl,
} from '@angular/forms';
import { Observable } from 'rxjs';
import { Folder } from 'src/app/acl-data/folder/folder.dto';

// tslint:disable-next-line: no-conflicting-lifecycle
@Component({
    selector: 'app-multiselect-wrapper',
    templateUrl: './multiselect-wrapper.component.html',
    styleUrls: ['./multiselect-wrapper.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiselectWrapperComponent),
            multi: true,
        },
    ],
})
export class MultiselectWrapperComponent
    implements OnChanges, ControlValueAccessor, DoCheck {
    @Input() value: any[];
    @Input() choiceListName: string;
    @Input() gerritServiceName: string;
    @Input() objectStore: string;
    @Input() label;
    @Input() disabled = false;
    @Input() allowSearchFilter = false;
    @Input() allowCheckAll = false;
    @Input() singleSelection = false;
    @Input() required = false;
    @Input() folders: Observable<Folder[]>;
    @Output() changed = new EventEmitter<any>();
    @Input() limitSelections: number;

    choices = [];
    selected = [];
    settings = {};
    onChanges = [];
    onTouched = [];
    touched = false;

    constructor(
        private choiceListService: ChoiceListService,
        private injector: Injector
    ) {}

    ngDoCheck() {
        const ngControl = this.injector.get(NgControl);
        if (!ngControl.control) {
            return;
        }
        this.touched = ngControl.control.touched;
    }

    private setOnChangeCallback(fn: any) {
        this.onChanges.push(fn);
    }

    private setOnTouchCallback(fn: any) {
        this.onTouched.push(fn);
    }

    setIsDisabled(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    writeValue(obj): void {
        // console.log(this.choices, obj);
        if (!!obj) {
            this.value = obj;
            this.getChoices();
        } else {
            this.value = [];
            this.getChoices();
        }
    }
    registerOnChange(fn: any): void {
        // console.log('registerOnChange');
        this.setOnChangeCallback(fn);
    }
    registerOnTouched(fn: any): void {
        this.setOnTouchCallback(fn);
    }
    setDisabledState?(isDisabled: boolean): void {
        this.setIsDisabled(isDisabled);
    }

    test = input => {
        console.log('TEST', input);
    };

    formChangedDeselectAll = (input: any) => {
        // console.log('Form formChangedDeselectAll', input, this.selected);

        const values = [];
        for (const change of this.onChanges) {
            change(values);
        }

        this.changed.emit(values);

        this.markAsTouched();
    };

    formChangedSelectAll = (input: any) => {
        // console.log(
        //     'Form formChangedSelectAll',
        //     input,
        //     this.selected,
        //     input.length,
        //     this.limitSelections
        // );
        if (
            this.limitSelections &&
            input &&
            input.length > this.limitSelections
        ) {
            input = input.slice(0, this.limitSelections);
            this.selected = input;
        }
        const values = input.map(v => v.id);
        for (const change of this.onChanges) {
            change(values);
        }

        this.changed.emit(values);

        this.markAsTouched();
    };

    formChanged = (input: any) => {
        // console.log('Form Changed', input, this.selected);
        if (
            this.limitSelections &&
            this.selected &&
            this.selected.length > this.limitSelections
        ) {
            this.selected = this.selected.slice(0, this.limitSelections);
        }
        const values = this.selected.map(v => v.id);
        for (const change of this.onChanges) {
            change(values);
        }

        this.changed.emit(values);

        this.markAsTouched();
    };

    formTouched = (input: any) => {
        this.touched = true;
        for (const touch of this.onTouched) {
            touch(input);
        }
    };

    markAsTouched() {
        this.touched = true;
        this.formTouched({});
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.settings = {
            singleSelection: this.singleSelection,
            text: this.label,
            lazyLoading: true,
            classes: 'myclass custom-dropdown',
            enableFilterSelectAll: false,
            enableCheckAll: this.allowCheckAll && !this.limitSelections,
            enableSearchFilter: this.allowSearchFilter,
            disabled: this.disabled,
            required: this.required,
            position: 'bottom',
            autoPosition: false,
        };

        if (this.folders) {
            // console.log('Got Folders', this.folders);
            this.folders
                .pipe(
                    map(v =>
                        v.map(row => {
                            return {
                                id: row.propertiesObj.FolderName,
                                itemName: row.propertiesObj.FolderName,
                            };
                        })
                    ),
                    take(1)
                )
                .subscribe(v => {
                    // console.log('Sub Folders', v);
                    this.choices = v;
                    this.selected = this.choices.filter(
                        s =>
                            !!this.value &&
                            this.value.indexOf(Number(s.id)) >= 0
                    );
                });
        } else {
            // console.log('Got choices');
            this.getChoices();
        }
    }

    private getChoices = () => {
        this.choiceListService
            .getByID(
                this.objectStore,
                this.choiceListName,
                this.gerritServiceName
            )
            .pipe(
                map(v => {
                    const result = Object.keys(v.value).reduce(
                        (acc, row) => [
                            ...acc,
                            {
                                id: Number(row) ? Number(row) : row,
                                itemName: v.value[row],
                            },
                        ],
                        []
                    );
                    return result;
                }),
                map(v => v.sort((a, b) => (a.itemName > b.itemName ? 1 : -1))),
                tap(v => console.log()),
                take(1)
            )
            .subscribe(v => {
                this.choices = v;
                this.selected = this.choices.filter(
                    s => !!this.value && this.value.indexOf(Number(s.id)) >= 0
                );
            });
    };
}
